import React, { useEffect, useState } from 'react';
import { Card, List, Switch, Typography, Spin, message } from 'antd';
import { TagOutlined } from '@ant-design/icons';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import PageHeader from '../../components/Common/PageHeader';
import auctionService from '../../services/auctionService';
import notificationService from '../../services/notificationService';
import { isPushSupported, subscribeToPush } from '../../utils/pushNotifications';

const { Text } = Typography;

const NotificationPreferences = () => {
    const [categories, setCategories] = useState([]);
    const [subscribedIds, setSubscribedIds] = useState([]);
    const [togglingId, setTogglingId] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [categoriesResponse, subscribedCategoryIds] = await Promise.all([
                auctionService.getCategoriesWithCounts(),
                notificationService.getSubscribedCategoryIds()
            ]);
            setCategories(categoriesResponse.data);
            setSubscribedIds(subscribedCategoryIds);
        } catch (error) {
            console.error('Error loading notification preferences:', error);
            message.error('Failed to load notification preferences');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleCategory = async (categoryId) => {
        const willSubscribe = !subscribedIds.includes(categoryId);

        setTogglingId(categoryId);
        try {
            if (willSubscribe && isPushSupported()) {
                try {
                    await subscribeToPush();
                } catch (pushError) {
                    console.error('Could not enable browser notifications:', pushError);
                }
            }

            const result = await notificationService.toggleCategorySubscription(categoryId);
            setSubscribedIds((prev) =>
                result.is_subscribed ? [...prev, categoryId] : prev.filter((id) => id !== categoryId)
            );
        } catch (error) {
            console.error('Error toggling category subscription:', error);
            message.error('Failed to update category subscription');
        } finally {
            setTogglingId(null);
        }
    };

    return (
        <DashboardLayout>
            <PageHeader
                title="Notification Preferences"
                description="Get notified as soon as a new auction is posted in your favorite categories."
            />

            <div style={{ padding: '0 24px 32px' }}>
                <Card
                    title="Subscribe to Categories"
                    style={{ borderRadius: 12 }}
                    extra={<Text type="secondary" style={{ fontSize: 13 }}>{subscribedIds.length} subscribed</Text>}
                >
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <Spin />
                        </div>
                    ) : (
                        <List
                            dataSource={categories}
                            renderItem={(category) => (
                                <List.Item
                                    actions={[
                                        <Switch
                                            key="toggle"
                                            checked={subscribedIds.includes(category.id)}
                                            loading={togglingId === category.id}
                                            onChange={() => handleToggleCategory(category.id)}
                                        />
                                    ]}
                                >
                                    <List.Item.Meta
                                        avatar={<TagOutlined style={{ fontSize: 18, color: '#8c8c8c' }} />}
                                        title={category.name}
                                        description={category.description}
                                    />
                                </List.Item>
                            )}
                        />
                    )}
                </Card>
            </div>
        </DashboardLayout>
    );
};

export default NotificationPreferences;
